import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useAudioStore } from '../stores/audioStore';
import { useNavigate } from 'react-router-dom';
import { Settings, Shield, Library, Loader2, Info, Vote, Download, Play, BarChart3 } from 'lucide-react';
import { SongGrid } from './SongGrid/SongGrid';
import { SongDetail } from './SongDetail/SongDetail';
import { AudioPlayer } from './AudioPlayer/AudioPlayer';
import { Settings as SettingsModal } from './Settings/Settings';
import { WelcomeModal } from './WelcomePage/WelcomeModal';
import { VotingModal } from './Voting/VotingModal';
import { TallyModal } from './Tally/TallyModal';
import { subsetsApi } from '../api/subsets';
import { welcomeApi } from '../api/welcome';
import { songsApi } from '../api/songs';
import type { SongSummary, Subset, SubsetWithSongs, WelcomePage } from '../types';

export function Home() {
  const { user, logout } = useAuthStore();
  const { playPlaylist, playFromCurrent } = useAudioStore();
  const navigate = useNavigate();
  const [selectedSong, setSelectedSong] = useState<SongSummary | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showVotingModal, setShowVotingModal] = useState(false);
  const [showTallyModal, setShowTallyModal] = useState(false);
  const [votingSubsetId, setVotingSubsetId] = useState<number | null>(null);
  const [welcomePage, setWelcomePage] = useState<WelcomePage | null>(null);
  const [subsets, setSubsets] = useState<Subset[]>([]);
  const [selectedTab, setSelectedTab] = useState<'library' | number>('library');
  const [selectedSubsetDetails, setSelectedSubsetDetails] = useState<SubsetWithSongs | null>(null);
  const [loadingSubsets, setLoadingSubsets] = useState(true);
  const [downloadingSubset, setDownloadingSubset] = useState<number | null>(null);
  const [voteRefreshKey, setVoteRefreshKey] = useState(0); // Increment to trigger vote refresh

  useEffect(() => {
    loadSubsets();
  }, []);

  useEffect(() => {
    // Load subset details when a subset tab is selected
    if (typeof selectedTab === 'number') {
      loadSubsetDetails(selectedTab);
    } else {
      setSelectedSubsetDetails(null);
    }
  }, [selectedTab]);

  useEffect(() => {
    // Stop playlist when subset changes (not when playlist mode changes)
    // Get current playlist state from store to avoid triggering on playlist mode changes
    const { isPlaylistMode: currentPlaylistMode, stopPlaylist: currentStopPlaylist } = useAudioStore.getState();
    if (currentPlaylistMode) {
      currentStopPlaylist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]); // Only trigger when selectedTab changes

  const loadSubsets = async () => {
    setLoadingSubsets(true);
    try {
      const data = await subsetsApi.getAllSubsets();
      setSubsets(data);

      // If user is not admin and has no visible subsets, select library
      // If user is not admin and has visible subsets, select first subset
      if (!user?.isAdmin && data.length > 0) {
        setSelectedTab(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load subsets:', error);
    } finally {
      setLoadingSubsets(false);
    }
  };

  const loadSubsetDetails = async (subsetId: number) => {
    try {
      const details = await subsetsApi.getSubsetById(subsetId);
      setSelectedSubsetDetails(details);
    } catch (error) {
      console.error('Failed to load subset details:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSelectSong = (song: SongSummary) => {
    setSelectedSong(song);
  };

  const handleCloseSongDetail = () => {
    setSelectedSong(null);
  };

  const handleTabChange = (tab: 'library' | number) => {
    setSelectedTab(tab);
  };

  const handleViewWelcome = async () => {
    try {
      const welcome = await welcomeApi.getCurrent();
      setWelcomePage(welcome);
      setShowWelcomeModal(true);
    } catch (error) {
      console.error('Failed to load welcome page:', error);
    }
  };

  const handleDownloadSubset = async (subsetId: number, subsetName: string) => {
    try {
      setDownloadingSubset(subsetId);
      await subsetsApi.downloadSubset(subsetId, subsetName);
    } catch (error) {
      console.error('Failed to download subset:', error);
      alert('Failed to download songs. Please try again.');
    } finally {
      setDownloadingSubset(null);
    }
  };

  const handlePlayAll = async () => {
    if (!selectedSubsetDetails) return;

    try {
      // Fetch all songs
      const allSongs = await songsApi.getAllSongs();

      // Filter and order songs by subset's songIds
      const orderedSongs = selectedSubsetDetails.songIds
        .map(id => allSongs.find(song => song.id === id))
        .filter((song): song is SongSummary => song !== undefined);

      if (orderedSongs.length === 0) {
        alert('No songs available in this subset.');
        return;
      }

      // Start playlist
      playPlaylist(orderedSongs);
    } catch (error) {
      console.error('Failed to load songs for playlist:', error);
      alert('Failed to start playlist. Please try again.');
    }
  };

  const handlePlayFromCurrent = async () => {
    if (!selectedSubsetDetails) return;

    try {
      // Fetch all songs
      const allSongs = await songsApi.getAllSongs();

      // Filter and order songs by subset's songIds
      const orderedSongs = selectedSubsetDetails.songIds
        .map(id => allSongs.find(song => song.id === id))
        .filter((song): song is SongSummary => song !== undefined);

      if (orderedSongs.length === 0) {
        alert('No songs available in this subset.');
        return;
      }

      // Start playlist from current song
      playFromCurrent(orderedSongs);
    } catch (error) {
      console.error('Failed to load songs for playlist:', error);
      alert('Failed to start playlist. Please try again.');
    }
  };

  const handleVoteSubmitted = () => {
    // Increment key to trigger vote refresh in SongGrid
    setVoteRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-monster-dark pb-24">
      {/* Header */}
      <header className="bg-monster-card border-b border-monster-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-monster-blue to-monster-crimson bg-clip-text text-transparent">
            Monstervision 2025
          </h1>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 hidden sm:block">
              Welcome, <span className="text-white font-medium">{user?.username}</span>
            </span>
            <button
              onClick={handleViewWelcome}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
              title="View Welcome Page"
            >
              <Info size={28} />
            </button>
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
              title="Settings"
            >
              <Settings size={28} />
            </button>
            {user?.isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="btn-primary text-sm flex items-center gap-2"
              >
                <Shield size={16} />
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button onClick={handleLogout} className="btn-secondary text-sm">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        {loadingSubsets ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-monster-blue" />
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="mb-6 overflow-x-auto">
              <div className="flex gap-2 min-w-max pb-2">
                {/* Library Tab (admin only) */}
                {user?.isAdmin && (
                  <button
                    onClick={() => handleTabChange('library')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                      selectedTab === 'library'
                        ? 'bg-monster-blue text-white'
                        : 'bg-monster-card text-gray-400 hover:bg-monster-card/70 hover:text-white'
                    }`}
                  >
                    <Library size={18} />
                    Library
                  </button>
                )}

                {/* Subset Tabs */}
                {subsets.map((subset) => (
                  <button
                    key={subset.id}
                    onClick={() => handleTabChange(subset.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedTab === subset.id
                        ? 'bg-monster-blue text-white'
                        : 'bg-monster-card text-gray-400 hover:bg-monster-card/70 hover:text-white'
                    }`}
                  >
                    {subset.name}
                    {subset.songCount !== undefined && (
                      <span className="ml-2 text-xs opacity-70">({subset.songCount})</span>
                    )}
                  </button>
                ))}

                {/* No subsets message for non-admin users */}
                {!user?.isAdmin && subsets.length === 0 && (
                  <div className="px-4 py-2 text-gray-400 text-sm">
                    No competitions available yet. Check back later!
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons (when subset tab is selected) */}
            {typeof selectedTab === 'number' && selectedSubsetDetails && (
              <div className="mb-6 flex flex-wrap gap-3">
                {/* Vote Button */}
                <button
                  onClick={() => {
                    setVotingSubsetId(selectedTab);
                    setShowVotingModal(true);
                  }}
                  disabled={!selectedSubsetDetails.votingOpen}
                  className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    selectedSubsetDetails.votingOpen
                      ? 'bg-monster-crimson text-white hover:bg-monster-crimson/80'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Vote size={20} />
                  {selectedSubsetDetails.votingOpen ? 'Cast Your Vote' : 'Voting Closed'}
                </button>

                {/* Vote Tally Button - only show when showLiveTally is enabled */}
                {selectedSubsetDetails.showLiveTally && (
                  <button
                    onClick={() => setShowTallyModal(true)}
                    className="px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 bg-monster-crimson text-white hover:bg-monster-crimson/80"
                  >
                    <BarChart3 size={20} />
                    Vote Tally
                  </button>
                )}

                {/* Download Button */}
                {selectedSubsetDetails.downloadable && (
                  <button
                    onClick={() => handleDownloadSubset(selectedTab, selectedSubsetDetails.name)}
                    disabled={downloadingSubset === selectedTab}
                    className="px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 bg-monster-blue text-white hover:bg-monster-blue/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadingSubset === selectedTab ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download size={20} />
                        Download Songs
                      </>
                    )}
                  </button>
                )}

                {/* Play All Button */}
                {selectedSubsetDetails.songCount && selectedSubsetDetails.songCount > 0 && (
                  <button
                    onClick={handlePlayAll}
                    className="px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 bg-monster-blue text-white hover:bg-monster-blue/80"
                  >
                    <Play size={20} />
                    Play All
                  </button>
                )}

                {/* Play From Current Button */}
                {selectedSubsetDetails.songCount && selectedSubsetDetails.songCount > 0 && (
                  <button
                    onClick={handlePlayFromCurrent}
                    className="px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 bg-monster-blue text-white hover:bg-monster-blue/80"
                  >
                    <Play size={20} />
                    Play From Current
                  </button>
                )}
              </div>
            )}

            {/* Song Grid */}
            <SongGrid
              onSelectSong={handleSelectSong}
              subsetSongIds={selectedSubsetDetails?.songIds}
              subsetId={typeof selectedTab === 'number' ? selectedTab : undefined}
              voteRefreshKey={voteRefreshKey}
            />
          </>
        )}
      </main>

      {/* Audio Player (bottom bar) */}
      <AudioPlayer />

      {/* Song Detail Panel */}
      {selectedSong && (
        <SongDetail song={selectedSong} onClose={handleCloseSongDetail} />
      )}

      {/* Settings Modal */}
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Welcome Modal */}
      {showWelcomeModal && welcomePage && (
        <WelcomeModal welcomePage={welcomePage} onClose={() => setShowWelcomeModal(false)} />
      )}

      {/* Voting Modal */}
      {showVotingModal && votingSubsetId !== null && (
        <VotingModal
          subsetId={votingSubsetId}
          onClose={() => {
            setShowVotingModal(false);
            setVotingSubsetId(null);
          }}
          onVoteSubmitted={handleVoteSubmitted}
        />
      )}

      {/* Tally Modal */}
      {showTallyModal && selectedTab !== null && typeof selectedTab === 'number' && (
        <TallyModal
          subsetId={selectedTab}
          onClose={() => setShowTallyModal(false)}
        />
      )}
    </div>
  );
}
